import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

/* ── [COL-RESIZE 2026-09-29, user] Excel-style drag-to-resize columns, shared by every table ──────
   Born in DataTable for Payments, then lifted here unchanged ([COL-RESIZE-ALL 2026-09-29, user]:
   "add the same column resize" to Delayed Payments, Stats, Refunds, Disputes, Inventory, My
   Inventory, New Sales, My Sales and the Application Log) so every table behaves identically.

   Drag a header's right edge to set that column's width; double-click the edge to hand the column
   back to automatic sizing. Widths are remembered per person in their own browser (localStorage;
   the key carries the user id), and only for columns someone actually dragged. A soft divider is
   always shown between headers so the drag point is visible; it turns green while hovered/dragged.

   How a width is applied (proven in headless Chrome before building): each dragged column gets a
   CSS variable on the <table>, e.g. --dtw-website: 150px. Its header cell reads it as
   width/min/max, and each body cell wraps its content in a clamp box of (width - cell padding) that
   cuts the text with an ellipsis. The clamp caps the cell's content width, which is what lets the
   column go NARROWER than its longest value in the table's automatic layout.

   A live drag writes ONLY that one variable on the table element: no React state per pointer move,
   so memoised rows never re-render mid-drag. React state is set once when a column first becomes
   dragged (so its cells gain the clamp box) and once on release (commit + save).

   Phones: callers pass enabled=false, so no handles and no stored widths apply there. */

export const MIN_COL_W = 48;
export const MAX_COL_W = 1200;

// A CSS custom-property name for a column key (keys are field names: letters, digits, _).
export const colVar = (key: string) => `--dtw-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

const clampW = (w: number) => Math.min(MAX_COL_W, Math.max(MIN_COL_W, Math.round(w)));

function readColWidths(storageKey?: string): Record<string, number> {
  if (!storageKey) return {};
  try {
    const v = JSON.parse(localStorage.getItem(storageKey) || '{}');
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const out: Record<string, number> = {};
    for (const [k, w] of Object.entries(v)) {
      const n = Number(w);
      if (Number.isFinite(n)) out[k] = clampW(n);
    }
    return out;
  } catch {
    return {}; // unreadable / blocked storage: automatic widths
  }
}

/* [LGP-SLIDE 2026-10-15] A save is also announced in this window, so tables mounted at the same time under ONE key share
   it at once: Lead Generator Performance → Leads Count keeps its US and ROW tables side by side for the slide, and a width
   dragged on one must be the other's too. Every other table has a key of its own (nothing else listens for its key). */
const SAVED_EVENT = 'dt-colw-saved';
type SavedDetail = { key: string; widths: Record<string, number>; from: object };

function writeColWidths(storageKey: string, widths: Record<string, number>, from: object): void {
  try {
    if (Object.keys(widths).length) localStorage.setItem(storageKey, JSON.stringify(widths));
    else localStorage.removeItem(storageKey);
  } catch {
    /* storage blocked (private mode / policy): the widths still apply for this visit */
  }
  try {
    window.dispatchEvent(new CustomEvent<SavedDetail>(SAVED_EVENT, { detail: { key: storageKey, widths, from } }));
  } catch { /* no window events: the other table takes the widths at its next mount */ }
}

const EMPTY_WIDTHS: Record<string, number> = {};

// A click on the handle must not reach the header's sort click.
const stopClick = (e: React.MouseEvent) => e.stopPropagation();

// The drag target on a header's right edge: 8px wide for an easy grab. touch-action none keeps a
// pen/touch drag from scrolling instead. It must be a DIRECT child of the <th> (the drag reads the
// header's width from its parent), and the <th> must be positioned (relative, or sticky).
// [COL-RESIZE-LINE 2026-09-29, user] "Show the line so I can see where to drag": a soft divider is
// ALWAYS drawn on the edge between headers (the user chose "header dividers"), and it turns green,
// wider and taller on hover and while dragging. None at rest after the LAST column (nothing to
// divide there); that column still resizes and still shows the green line on hover.
export const ResizeHandle = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  touch-action: none;
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    top: 20%;
    bottom: 20%;
    right: 0;
    width: 1px;
    border-radius: 1px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(250, 250, 250, 0.2)' : 'rgba(26, 33, 30, 0.22)')};
    transition: background-color 150ms ease, width 150ms ease, top 150ms ease, bottom 150ms ease;
  }

  th:last-child > &::after {
    opacity: 0;
  }

  &:hover::after,
  &[data-active='1']::after,
  th:last-child > &:hover::after,
  th:last-child > &[data-active='1']::after {
    top: 12%;
    bottom: 12%;
    width: 2px;
    opacity: 1;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

// The clamp box inside a dragged column's body cells: caps the content at the column's width and
// cuts the overflow with an ellipsis (the user's choice: rows stay one line tall, like Excel).
// $deep also gives the ellipsis to nested one-line blocks (e.g. the Application Log's date over
// time, name over email), whose text is not the box's own and would otherwise just be clipped.
export const CutCell = styled.div<{ $deep?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${(p) => (p.$deep ? '& div { overflow: hidden; text-overflow: ellipsis; }' : '')}
`;

// The same cut for a dragged column's header label; the sort arrow beside it stays visible.
export const CutLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// [COL-RESIZE-TITLE 2026-09-29] GlobalTooltip keeps an element's title in `__ttTitle` while its tip
// is showing (and writes it back on leave). A title changed mid-hover (e.g. a data refresh that lands
// while the pointer rests on a cell) must go to that stash, or the leave would restore the old one.
const getTitle = (n: HTMLElement): string | null =>
  ((n as any).__ttTitle != null ? (n as any).__ttTitle : n.getAttribute('title'));
const setTitle = (n: HTMLElement, t: string | null): void => {
  if ((n as any).__ttTitle != null) {
    if (t == null) delete (n as any).__ttTitle; else (n as any).__ttTitle = t;
    return;
  }
  if (t == null) n.removeAttribute('title'); else n.setAttribute('title', t);
};

/** Inline style for a dragged column's clamp box (`cellPadX` = the body cell's left + right
 *  padding). Centred / right-aligned columns keep their alignment inside a wider cell. */
export function cutCellStyle(v: string, cellPadX: number, align?: string): React.CSSProperties {
  return {
    maxWidth: `calc(var(${v}) - ${cellPadX}px)`,
    margin: align === 'center' ? '0 auto' : align === 'right' ? '0 0 0 auto' : undefined,
  };
}

export interface UseColumnResizeOptions {
  /** localStorage key the widths live under (include the user id). Absent = feature off. */
  storageKey?: string;
  /** False on phones: no handles and no stored widths applied, so the phone view is unchanged. */
  enabled: boolean;
  /** Keys of the columns currently drawn, in order. */
  columnKeys: string[];
  /** The header cell's left + right padding (the header label clamps inside it). */
  headerPadX: number;
  /** A value whose change must re-run the "is this cell cut?" pass, e.g. the rows array. */
  refresh?: unknown;
  /** Hover titles (full text on cut cells, the hint on the handle). False where the user banned
   *  tooltips: the Application Log ([NO TOOLTIPS 2026-09-09, user]). Default true. */
  hoverTitles?: boolean;
}

export interface ColumnResize {
  /** Resizing is on here (show the handles). */
  active: boolean;
  /** Column key -> its width variable, for the dragged columns on screen. Stable between drags. */
  cut: Record<string, string> | undefined;
  /** Attach to the <table> (a drag writes its width variable here). */
  tableElRef: React.MutableRefObject<HTMLTableElement | null>;
  /** The width variables, for the <table>'s style. */
  tableStyle: React.CSSProperties | undefined;
  /** Width/min/max for a dragged column's <th>; undefined for automatic columns. */
  thStyle: (key: string) => React.CSSProperties | undefined;
  /** The clamp for a dragged column's header label box; undefined for automatic columns. */
  labelStyle: (key: string) => React.CSSProperties | undefined;
  /** Props for the <ResizeHandle> of a column. */
  handleProps: (key: string, label: string) => React.HTMLAttributes<HTMLSpanElement> & { role: string };
}

export function useColumnResize({
  storageKey, enabled, columnKeys, headerPadX, refresh, hoverTitles = true,
}: UseColumnResizeOptions): ColumnResize {
  const canResize = !!storageKey && enabled;
  const tableElRef = useRef<HTMLTableElement | null>(null);
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => readColWidths(storageKey));
  const colWidthsRef = useRef(colWidths);
  colWidthsRef.current = colWidths;

  // A different person signing in on the same browser gets their own saved widths.
  const loadedKeyRef = useRef(storageKey);
  useEffect(() => {
    if (loadedKeyRef.current === storageKey) return;
    loadedKeyRef.current = storageKey;
    setColWidths(readColWidths(storageKey));
  }, [storageKey]);

  // [LGP-SLIDE 2026-10-15] Another table under the same key saved its widths: take them (see writeColWidths).
  const selfRef = useRef({});
  useEffect(() => {
    if (!storageKey) return undefined;
    const onSaved = (e: Event) => {
      const d = (e as CustomEvent<SavedDetail>).detail;
      if (!d || d.key !== storageKey || d.from === selfRef.current) return;
      setColWidths({ ...d.widths });
    };
    window.addEventListener(SAVED_EVENT, onSaved);
    return () => window.removeEventListener(SAVED_EVENT, onSaved);
  }, [storageKey]);

  const liveWidths = canResize ? colWidths : EMPTY_WIDTHS;
  // Only columns actually drawn. A saved width for a column not on screen (e.g. an admin extra
  // column that was removed) stays saved and simply isn't used.
  const keysSig = columnKeys.join('|');
  const cutSig = columnKeys.filter((k) => liveWidths[k] != null).join('|');
  const cut = useMemo(() => {
    if (!cutSig) return undefined;
    const m: Record<string, string> = {};
    for (const k of cutSig.split('|')) m[k] = colVar(k);
    return m;
  }, [cutSig]);
  const tableVars = useMemo(() => {
    const s: Record<string, string> = {};
    for (const [k, w] of Object.entries(liveWidths)) s[colVar(k)] = `${w}px`;
    return s as React.CSSProperties;
  }, [liveWidths]);

  const dragRef = useRef<{
    key: string; pointerId: number; startX: number; startW: number; w: number; moved: boolean; scale: number;
    cursor: string; select: string; tip: string | null;
  } | null>(null);

  const onResizeDown = useCallback((e: React.PointerEvent<HTMLSpanElement>, key: string) => {
    if (e.button !== 0) return; // primary button / touch contact only
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const th = handle.parentElement;
    if (!th) return;
    // [COL-RESIZE-TIP 2026-09-29, user] "Tooltip when someone stays on the line, not while moving
    // it." GlobalTooltip hides on pointerdown, but while the pointer is captured the browser keeps
    // firing pointerover on the handle, which re-arms the hint mid-drag. So the handle has NO title
    // for the whole press (restored on release): nothing for the tooltip to show while dragging.
    const tip = handle.getAttribute('title');
    if (tip != null) handle.removeAttribute('title');
    // [ZOOM-SAFE 2026-10-14] A table inside a CSS-zoomed page (Lead Management's auto-fit): getBoundingClientRect and the
    // pointer are in SCREEN px, the width variable is in page px. Measure the zoom (≈ 1 everywhere else → exactly as before).
    const rectW = th.getBoundingClientRect().width;
    const ratio = th.offsetWidth > 0 ? rectW / th.offsetWidth : 1;
    const scale = Math.abs(ratio - 1) < 0.01 ? 1 : ratio;
    dragRef.current = {
      key, pointerId: e.pointerId, startX: e.clientX, startW: rectW / scale,
      w: 0, moved: false, cursor: document.body.style.cursor, select: document.body.style.userSelect, tip, scale,
    };
    try { handle.setPointerCapture(e.pointerId); } catch { /* the drag still works while over the handle */ }
    handle.dataset.active = '1';
  }, []);

  const onResizeMove = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = (e.clientX - d.startX) / d.scale; // [ZOOM-SAFE] screen px → page px
    if (!d.moved) {
      if (Math.abs(dx) < 3) return; // a click or double-click is not a drag
      d.moved = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    const w = clampW(d.startW + dx);
    if (w === d.w) return;
    const first = d.w === 0 && colWidthsRef.current[d.key] == null;
    d.w = w;
    tableElRef.current?.style.setProperty(colVar(d.key), `${w}px`);
    // First move on a column that was automatic: ONE render so its cells gain the clamp box. After
    // that, only the variable above moves until release.
    if (first) setColWidths((prev) => ({ ...prev, [d.key]: w }));
  }, []);

  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    delete e.currentTarget.dataset.active;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    // [COL-RESIZE-TIP 2026-09-29] The hint comes back once the line is let go (see onResizeDown).
    if (d.tip != null) e.currentTarget.setAttribute('title', d.tip);
    if (!d.moved) return;
    document.body.style.cursor = d.cursor;
    document.body.style.userSelect = d.select;
    const next = { ...colWidthsRef.current, [d.key]: d.w };
    setColWidths(next);
    if (storageKey) writeColWidths(storageKey, next, selfRef.current);
  }, [storageKey]);

  // Double-click the edge: back to automatic sizing (and forgotten).
  const onResizeReset = useCallback((e: React.MouseEvent<HTMLSpanElement>, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (colWidthsRef.current[key] == null) return;
    const next = { ...colWidthsRef.current };
    delete next[key];
    tableElRef.current?.style.removeProperty(colVar(key));
    setColWidths(next);
    if (storageKey) writeColWidths(storageKey, next, selfRef.current);
  }, [storageKey]);

  // Leaving the page mid-drag must not leave the whole page on a resize cursor.
  useEffect(() => () => {
    const d = dragRef.current;
    if (d && d.moved) {
      document.body.style.cursor = d.cursor;
      document.body.style.userSelect = d.select;
    }
  }, []);

  // Full text on hover for the cells the ellipsis actually cut. GlobalTooltip reads `title` the
  // instant the pointer arrives (capture phase), so the title must already be there BEFORE the hover:
  // it is set here after each paint of new data or widths, never in a hover handler. Our titles are
  // marked so a later widening takes them back off.
  // [COL-RESIZE-TITLE 2026-09-29] Two cases the box alone missed (found by the real-page harness):
  //  - a cell that cuts INSIDE a flex row (e.g. Inventory's website: icons + value) marks the value
  //    `data-dt-cut-inner`; the box itself then never overflows, so the inner one decides "cut";
  //  - text inside the box that carries its OWN title (Payments' "Click to edit") would win the hover
  //    (the tooltip takes the closest title), so while cut such a title becomes "<full text>\n<its
  //    own>", and gets its original back once the column is wide enough again.
  useLayoutEffect(() => {
    const table = tableElRef.current;
    if (!table || !cut || !hoverTitles) return;
    table.querySelectorAll<HTMLElement>('[data-dt-cut]').forEach((el) => {
      const inner = Array.from(el.querySelectorAll<HTMLElement>('[data-dt-cut-inner]'));
      const isCut = el.scrollWidth > el.clientWidth + 1 || inner.some((n) => n.scrollWidth > n.clientWidth + 1);
      const full = isCut ? (el.innerText || el.textContent || '').trim() : '';
      const ours = el.dataset.autoTitle === '1';
      if (isCut) {
        if (ours || getTitle(el) == null) {
          setTitle(el, full);
          el.dataset.autoTitle = '1';
        }
      } else if (ours) {
        setTitle(el, null);
        delete el.dataset.autoTitle;
      }
      // Titled elements that hold visible text (icon-only buttons keep their own hint untouched). All
      // descendants are scanned, not just [title]: an element whose tip is showing right now has its
      // title held in the tooltip's stash (no attribute), and must still be updated.
      el.querySelectorAll<HTMLElement>('*').forEach((n) => {
        if (n.dataset.dtOrigTitle == null && getTitle(n) == null) return;
        const own = n.dataset.dtOrigTitle ?? getTitle(n) ?? '';
        if (isCut && full && (n.innerText || '').trim() && !own.includes(full)) {
          if (n.dataset.dtOrigTitle == null) n.dataset.dtOrigTitle = own;
          setTitle(n, own ? `${full}\n${own}` : full);
        } else if (n.dataset.dtOrigTitle != null) {
          setTitle(n, n.dataset.dtOrigTitle || null);
          delete n.dataset.dtOrigTitle;
        }
      });
    });
  }, [refresh, colWidths, cut, keysSig, hoverTitles]);

  const thStyle = useCallback((key: string): React.CSSProperties | undefined => {
    const v = cut?.[key];
    return v ? { width: `var(${v})`, minWidth: `var(${v})`, maxWidth: `var(${v})`, boxSizing: 'border-box' } : undefined;
  }, [cut]);

  const labelStyle = useCallback((key: string): React.CSSProperties | undefined => {
    const v = cut?.[key];
    return v ? { maxWidth: `calc(var(${v}) - ${headerPadX}px)`, overflow: 'hidden' } : undefined;
  }, [cut, headerPadX]);

  const handleProps = useCallback((key: string, label: string) => ({
    role: 'separator',
    'aria-orientation': 'vertical' as const,
    'aria-label': `Resize ${label}`,
    title: hoverTitles ? 'Drag to resize · double-click to reset' : undefined,
    onPointerDown: (e: React.PointerEvent<HTMLSpanElement>) => onResizeDown(e, key),
    onPointerMove: onResizeMove,
    onPointerUp: onResizeEnd,
    onPointerCancel: onResizeEnd,
    onClick: stopClick,
    onDoubleClick: (e: React.MouseEvent<HTMLSpanElement>) => onResizeReset(e, key),
  }), [hoverTitles, onResizeDown, onResizeMove, onResizeEnd, onResizeReset]);

  return {
    active: canResize,
    cut,
    tableElRef,
    tableStyle: canResize ? tableVars : undefined,
    thStyle,
    labelStyle,
    handleProps,
  };
}
